import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Search, Loader2, MapPin, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getAutocomplete,
  getPlaceDetails,
  createAddressFromPlace,
  buildAddressRequest,
  detectCountryCode,
} from '@/lib/address-api';
import type { Address, AutocompletePrediction, CreateAddressRequest } from '@vn-propane/shared/client';
import { SUPPORTED_COUNTRIES, getCountryName } from '@vn-propane/shared/countries';

/**
 * Selected address data - can be either a saved Address or pending CreateAddressRequest
 */
export interface AddressData {
  /** For display purposes */
  formattedAddress: string;
  /** If this is an existing saved address */
  address?: Address;
  /** If this is a new address to be created (deferred creation mode) */
  createRequest?: CreateAddressRequest;
}

interface AddressInputProps {
  organizationId: string;
  /** Current value - either saved address or pending address data */
  value?: AddressData | null;
  /** Called when address changes */
  onChange: (data: AddressData | null) => void;
  /**
   * When true, don't create address in database - return CreateAddressRequest instead.
   * Use this for ACID-compliant flows where address should be created in a transaction.
   */
  deferCreation?: boolean;
  defaultCountry?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function AddressInput({
  organizationId,
  value,
  onChange,
  deferCreation = false,
  defaultCountry = 'VN',
  placeholder,
  className,
  disabled = false,
}: AddressInputProps) {
  const { t } = useTranslation(['admin']);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());

  // Update selected country when defaultCountry prop changes
  useEffect(() => {
    setSelectedCountry(defaultCountry);
  }, [defaultCountry]);

  // Clear predictions when country changes
  useEffect(() => {
    setPredictions([]);
    setQuery('');
    // Generate new session token when country changes
    sessionTokenRef.current = crypto.randomUUID();
  }, [selectedCountry]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddresses = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setPredictions([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const results = await getAutocomplete(organizationId, searchQuery, {
          country: selectedCountry,
        });
        setPredictions(results);
      } catch (err) {
        console.error('Failed to search addresses:', err);
        setPredictions([]);
        setError(t('addressInput.errorFetching'));
      } finally {
        setLoading(false);
      }
    },
    [organizationId, selectedCountry, t]
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchAddresses(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchAddresses]);

  const handleSelect = async (prediction: AutocompletePrediction) => {
    setSelecting(true);
    setError(null);
    setIsOpen(false);

    try {
      // Fetch place details
      const details = await getPlaceDetails(organizationId, prediction.place_id);
      if (!details) {
        setError(t('addressInput.errorCreating'));
        setSelecting(false);
        return;
      }

      // Determine country from address components, fallback to selected country
      const countryCode = detectCountryCode(details.address_components) || selectedCountry;

      // Generate new session token for next search
      sessionTokenRef.current = crypto.randomUUID();

      if (deferCreation) {
        // Return CreateAddressRequest for ACID-compliant flows
        const createRequest = buildAddressRequest(details, countryCode);
        onChange({
          formattedAddress: details.formatted_address,
          createRequest,
        });
      } else {
        // Create address in the database immediately
        const address = await createAddressFromPlace(organizationId, details, countryCode);
        if (!address) {
          setError(t('addressInput.errorCreating'));
          setSelecting(false);
          return;
        }
        onChange({
          formattedAddress: address.formattedAddress,
          address,
        });
      }

      setQuery('');
      setPredictions([]);
    } catch (err) {
      console.error('Failed to create address:', err);
      setError(t('addressInput.errorCreating'));
    } finally {
      setSelecting(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  };

  // Show selected address
  if (value) {
    return (
      <div className={cn('relative', className)}>
        <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
          <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{value.formattedAddress}</p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground p-1 rounded-sm hover:bg-muted"
              title={t('addressInput.change')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="flex gap-2">
        {/* Country selector */}
        <Select
          value={selectedCountry}
          onValueChange={setSelectedCountry}
          disabled={disabled || selecting}
        >
          <SelectTrigger className="w-44 flex-shrink-0">
            <SelectValue>
              <span className="flex items-center gap-2">
                <span className={`fi fi-${selectedCountry.toLowerCase()} rounded-sm`} />
                <span className="text-sm truncate">{getCountryName(selectedCountry)}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_COUNTRIES.map((code) => (
              <SelectItem key={code} value={code}>
                <span className="flex items-center gap-2">
                  <span className={`fi fi-${code.toLowerCase()} rounded-sm`} />
                  <span>{getCountryName(code)}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search input */}
        <div className="relative flex-1">
          {loading || selecting ? (
            <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          )}
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder || t('addressInput.searchPlaceholder')}
            disabled={disabled || selecting}
            className="pl-10 py-2"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive mt-1">{error}</p>
      )}

      {isOpen && query.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-auto">
          {predictions.length > 0 ? (
            <ul className="py-1">
              {predictions.map((prediction) => (
                <li key={prediction.place_id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(prediction)}
                    disabled={selecting}
                    className="w-full px-3 py-2 text-left hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{prediction.main_text}</p>
                        {prediction.secondary_text && (
                          <p className="text-xs text-muted-foreground truncate">
                            {prediction.secondary_text}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : !loading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('addressInput.noResults')}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
